import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, mapObjectTypeToCivildrawObjectType, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { EditorMode, type MapObject, type Project } from '~/common/types';
import Icon, { type IconName } from '~/components2/Icon';
import { useCivilDrawStore } from '../../civilDrawStoreContext';

interface ObjActionsProps {
  currentObject: MapObject;
}

const tooltipClassNames =
  'absolute hidden whitespace-nowrap rounded bg-grey-60 px-2 py-1 text-white opacity-50 group-focus-within:block group-hover:block';

interface ButtonProps {
  icon: IconName;
  label: string;
  className?: string;
  onClick: () => void;
  'data-testid'?: string;
}

function ObjActionButton({ icon, label, className, onClick, 'data-testid': dataTestId }: ButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      className="flex cursor-pointer appearance-none rounded border-0 bg-transparent p-0 focus-ring"
      onClick={onClick}
      data-testid={dataTestId}
    >
      <Icon icon={icon} className={className} size={20} />
    </button>
  );
}

export default function ObjActions({ currentObject }: ObjActionsProps) {
  const store = useCivilDrawStore();
  const { currentObjectId, currentMapId, project, hiddenObjects } = useSnapshot(store) as typeof store;
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center text-xs">
        Object
        <Icon icon="chevronRight" />
        <span className="text-secondary">{currentObject.type[0].toUpperCase() + currentObject.type.slice(1)}</span>
      </div>
      <div className="relative flex items-center justify-end gap-3">
        <div className="group">
          <ObjActionButton
            icon="delete"
            label="Delete object"
            className="hover:text-red-500"
            data-testid="civildraw-object-delete"
            onClick={() => {
              store.deleteObject(currentObject, true);
              store.setEditorMode(EditorMode.None);
              const analyticsProject = project as unknown as Project;
              captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectDeleted, {
                project_id: analyticsProject.id,
                organization_id: analyticsProject.organization || undefined,
                object_type: mapObjectTypeToCivildrawObjectType(currentObject.type),
                ...(currentMapId && { map_id: currentMapId }),
                civildraw_use_new_drawing_canvas: false,
              });
            }}
          />

          <div style={{ transform: 'translate(-50%, 10%)' }} className={tooltipClassNames}>
            Delete Object
          </div>
        </div>
        <div className="group">
          <ObjActionButton
            icon="visible"
            label={currentObjectId && hiddenObjects.includes(currentObjectId) ? 'Show object' : 'Hide object'}
            className="hover:text-primary"
            data-testid="civildraw-object-toggle-visibility"
            onClick={() => {
              if (currentObjectId) {
                store.toggleHiddenObjects(currentObjectId);
                store.resetCurrentObjectId();
                const analyticsProject = project as unknown as Project;
                captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectVisibilityToggled, {
                  project_id: analyticsProject.id,
                  organization_id: analyticsProject.organization || undefined,
                  object_type: mapObjectTypeToCivildrawObjectType(currentObject.type),
                  hidden: !hiddenObjects.includes(currentObjectId), // Check looks backwards because hiddenObject won't update with the change until next render
                  ...(currentMapId && { map_id: currentMapId }),
                });
              }
            }}
          />
          <div style={{ transform: 'translate(-50%, 10%)' }} className={tooltipClassNames}>
            Toggle object on/off.
          </div>
        </div>
        <div className="group">
          <ObjActionButton
            icon="minus"
            label="Close details"
            className="hover:text-primary"
            data-testid="civildraw-object-close-details"
            onClick={() => store.resetCurrentObjectId()}
          />
          <div style={{ transform: 'translate(-75%, 10%)' }} className={tooltipClassNames}>
            Close Details
          </div>
        </div>
      </div>
    </div>
  );
}
